import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { BUCKET_LABELS, api, useApiQuery } from "../api";
import { useWalletScope } from "../wallet";
import type { Bucket, Int64, MissionProgress, WalletMissions, WalletState } from "../api";
import { Button, EmptyState, SectionTitle, Skeleton, Stat, cx } from "../components";
import { ratio } from "../format";
import { useCardEntrance, useCountUp } from "../motion";
import { AsyncBoundary, PageHeader } from "../shell";
import { MissionsCarte } from "./MissionsCarte";
import type { Geste } from "./MissionsCarte";
import { MissionsCloses } from "./MissionsCloses";
import styles from "./MissionsPage.module.css";

/**
 * Les trois seaux que les gestes de mission font bouger.
 *
 * `expired` n'y est pas : aucune consommation, aucune clôture, aucune
 * annulation ne l'alimente — seule l'expiration d'un pack le fait, et elle ne
 * se déclenche pas depuis cet écran. Le montrer ici laisserait croire qu'il
 * participe au mouvement qu'on est en train de regarder.
 */
const SEAUX_SUIVIS: Bucket[] = ["available", "reserved", "consumed"];

/** Quel panneau est ouvert, et sur quelle mission. Un seul à la fois. */
type PanneauOuvert = { missionID: string; geste: Geste };

/**
 * Écran « suivi des missions ».
 *
 * Il comble les deux dernières étapes du parcours — consommer, clôturer — qui
 * n'existaient jusqu'ici qu'en `curl`. Tout y est construit autour d'une seule
 * démonstration : **les tokens ne disparaissent pas, ils changent de seau**.
 * D'où les soldes du portefeuille en tête d'écran, relus après chaque geste :
 * une consommation fait baisser le réservé et monter le consommé du même
 * montant, une clôture ramène le reliquat au disponible.
 */
export function MissionsPage() {
  const { walletId, links } = useWalletScope();
  const missions = useApiQuery((options) => api.listWalletMissions(walletId, options), [walletId]);
  const portefeuille = useApiQuery(
    (options) => api.getWalletState(walletId, options),
    [walletId],
  );

  const [ouvert, setOuvert] = useState<PanneauOuvert | null>(null);

  const relireTout = useCallback(() => {
    missions.reload();
    portefeuille.reload();
  }, [missions, portefeuille]);

  // Un geste abouti change à la fois l'état de la mission et les seaux du
  // portefeuille : les deux lectures repartent, et le panneau se referme parce
  // que son intention est consommée.
  const apresGeste = useCallback(() => {
    setOuvert(null);
    relireTout();
  }, [relireTout]);

  const enChargement = missions.loading || portefeuille.loading;

  return (
    <>
      <PageHeader
        eyebrow="Projets"
        title="Suivi des projets"
        description="L'équipe et le budget de chaque projet : déclarer le travail réalisé, clôturer ce qui est fini, annuler ce qui ne se fera pas."
        actions={
          <>
            <Button
              variant="ghost"
              onClick={relireTout}
              loading={enChargement && missions.data !== null}
            >
              Actualiser
            </Button>
            {/* Commander depuis cet écran n'était possible que quand il n'y
                avait rien à suivre : les deux liens vivaient dans les états
                vides, et disparaissaient dès la première mission. */}
            <Link
              className={cx(styles.lienAction, styles.lienPrincipal)}
              to={links.commande}
            >
              Nouveau projet
            </Link>
          </>
        }
      />

      <section className={styles.section} aria-labelledby="titre-seaux">
        <SectionTitle
          id="titre-seaux"
          description="Consommer, clôturer ou annuler déplace des tokens d'un compteur à l'autre : votre total ne change pas."
        >
          Votre solde
        </SectionTitle>

        <AsyncBoundary
          state={portefeuille}
          errorTitle="Soldes illisibles"
          skeleton={
            <div className={styles.seaux} aria-busy="true">
              <Skeleton variant="block" height="8rem" />
              <Skeleton variant="block" height="8rem" />
              <Skeleton variant="block" height="8rem" />
            </div>
          }
        >
          {(etat) => <Seaux etat={etat} />}
        </AsyncBoundary>
      </section>

      <AsyncBoundary
        state={missions}
        errorTitle="Missions illisibles"
        skeleton={<SqueletteMissions />}
        isEmpty={(donnees) => donnees.missions.length === 0}
        empty={
          <section className={styles.section}>
            <EmptyState
              icon="📋"
              title="Aucune mission sur ce portefeuille."
              description="Rien n'a encore été commandé : il n'y a donc rien à consommer ni à clôturer."
              action={
                // Un `Link` et non un `Button` : c'est une navigation, et un
                // `<button>` casserait le clic du milieu et l'ouverture dans un
                // onglet.
                <Link className={styles.lienAction} to={links.commande}>
                  Créer un projet
                </Link>
              }
            />
          </section>
        }
      >
        {(donnees) => (
          <Sections
            donnees={donnees}
            ouvert={ouvert}
            onOuvrir={setOuvert}
            onFait={apresGeste}
          />
        )}
      </AsyncBoundary>
    </>
  );
}

/** Silhouette de l'écran : la même forme que ce qui va arriver. */
function SqueletteMissions() {
  return (
    <div className={styles.section} aria-busy="true">
      <Skeleton variant="block" height="18rem" />
      <Skeleton variant="block" height="18rem" />
    </div>
  );
}

/** Les trois seaux suivis, chacun avec sa part du total entré. */
function Seaux({ etat }: { etat: WalletState }) {
  return (
    <div className={styles.seaux}>
      {SEAUX_SUIVIS.map((seau) => (
        <TuileSeau
          key={seau}
          seau={seau}
          montant={etat.balance[seau]}
          total={etat.balance.total}
        />
      ))}
    </div>
  );
}

/**
 * Une tuile de seau dont le chiffre monte jusqu'à sa valeur.
 *
 * Le compteur est ce qui rend le mouvement lisible : après une clôture, on voit
 * le disponible grimper et le réservé descendre, au lieu de deux nombres qui
 * changent sans qu'on sache lequel a bougé.
 */
function TuileSeau({
  seau,
  montant,
  total,
}: {
  seau: Bucket;
  montant: Int64;
  total: Int64;
}) {
  // L'élément reste vide : c'est `useCountUp` qui écrit son texte, et qui pose
  // la chaîne exacte de l'API à la dernière image — aucun solde n'est arrondi.
  const compteur = useCountUp<HTMLSpanElement>(montant);

  return (
    <Stat
      label={BUCKET_LABELS[seau]}
      value={<span ref={compteur} className="lde-tabular" />}
      unit="tokens"
      bucket={seau}
      ratio={ratio(montant, total)}
    />
  );
}

type ProprietesSections = {
  donnees: WalletMissions;
  ouvert: PanneauOuvert | null;
  onOuvrir: (ouvert: PanneauOuvert | null) => void;
  onFait: () => void;
};

/**
 * Les missions, séparées en deux : celles sur lesquelles on peut encore agir,
 * et les autres.
 *
 * La séparation n'est pas cosmétique. Elle porte toute la règle de l'écran :
 * seules les missions `ordered` acceptent les trois gestes, les closes rendent
 * `409 invalid_transition`. Mettre les unes au-dessus et les autres dans un
 * tableau sans action rend cette règle visible sans l'écrire.
 */
function Sections({ donnees, ouvert, onOuvrir, onFait }: ProprietesSections) {
  const { links } = useWalletScope();
  const toutes = donnees.missions;
  const enCours = toutes.filter((m) => m.status === "ordered");
  const closes = toutes.filter((m) => m.status !== "ordered");

  const liste = useCardEntrance<HTMLDivElement>({ dependencies: [enCours.length] });

  return (
    <>
      <section className={styles.section} aria-labelledby="titre-en-cours">
        <SectionTitle
          id="titre-en-cours"
          description={
            enCours.length === 0
              ? "Aucune enveloppe ouverte : tout est clos ou annulé."
              : `${enCours.length} projet${enCours.length > 1 ? "s" : ""} en cours sur ${toutes.length}. Seuls ceux-là acceptent une consommation, une clôture ou une annulation.`
          }
        >
          Projets en cours
        </SectionTitle>

        <div ref={liste} className={styles.liste}>
          {enCours.map((mission: MissionProgress) => (
            <MissionsCarte
              key={mission.id}
              mission={mission}
              geste={ouvert?.missionID === mission.id ? ouvert.geste : null}
              onGeste={(geste) =>
                onOuvrir(geste === null ? null : { missionID: mission.id, geste })
              }
              onFait={onFait}
            />
          ))}
        </div>

        {enCours.length === 0 && (
          <EmptyState
            compact
            title="Plus rien en cours."
            description="Tous les projets sont clos ou annulés : ce qui n'a pas été consommé est déjà revenu dans votre solde."
            action={
              <Link className={styles.lienAction} to={links.commande}>
                Créer un projet
              </Link>
            }
          />
        )}
      </section>

      <section className={styles.section} aria-labelledby="titre-closes">
        <SectionTitle id="titre-closes">
          Historique des projets
        </SectionTitle>
        <MissionsCloses missions={closes} />
      </section>
    </>
  );
}
